import { LocalAtRecord, Mission } from '../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { alertError, alertSuccess } from '../../alertDispatcher';
import logger from '../../logger';
import { MissionStatus } from '../../types/types';

/**
 * Service for handling boundary approval operations
 */
export class BoundaryApprovalService {
  /**
   * Approves a job's boundary by generating KML and updating Airtable
   * @param jobRecord The job record to approve
   * @param mission The mission object with the current boundaries
   * @returns Success status
   */
  async approveJobBoundary(jobRecord: LocalAtRecord<JobsFieldSet>, mission: Mission): Promise<boolean> {
    try {
      const missionStatus: MissionStatus = 'completed';
      const missionName = mission.getMissionName(missionStatus);

      // Generate KML from current mission state
      const kml = mission.to_kml({ insert_boxes: true });

      if (!kml) {
        alertError('Failed to generate KML from mission');

        return false;
      }

      const kmz = await kml.toKMZ(`${missionName}.kml`);

      if (!kmz) {
        throw new Error('Could not create KMZ');
      }

      // Clear existing Exe Mission attachments
      await jobRecord.clear_attachments('Exe Mission');

      // Create new attachment
      const attachment = await jobRecord.new_attachment('Exe Mission');
      // Save our file into it (make the jobRecord dirty)
      await attachment.write(
        missionName,
        'kmz',
        new Blob([kmz as BlobPart], { type: 'application/vnd.google-earth.kmz' }),
      );

      // Set the "Bdy Approved for Send?" field to True
      await jobRecord.set('Bdy Approved for Send?', true);

      // Sync changes to Airtable
      if (jobRecord.dirty) {
        await jobRecord.sync();
      }

      alertSuccess(`Successfully approved boundary for job ${jobRecord.get('Name') || jobRecord.id}`);

      await logger.log('BOUNDARY_APPROVED', {
        jobId: jobRecord.id,
        jobName: jobRecord.get('Name'),
      });

      return true;
    } catch (err) {
      console.error('Error approving job boundary:', err);
      alertError(`Failed to approve boundary: ${err instanceof Error ? err.message : 'Unknown error'}`);

      await logger.log('BOUNDARY_APPROVAL_FAILED', {
        jobId: jobRecord.id,
        error: err instanceof Error ? err.message : 'Unknown error',
      });

      return false;
    }
  }

  /**
   * Validates that a mission can be approved
   * @param mission The mission to validate
   * @returns Validation result with errors if any
   */
  validateMissionForApproval(mission: Mission): {
    valid: boolean;
    errors: string[];
    defectiveGeometries: import('geojson').Feature<GeoJSON.Point | GeoJSON.Polygon>[];
    warningGeometries: import('geojson').Feature<GeoJSON.Point | GeoJSON.Polygon>[];
  } {
    const validationResult = mission.validateBeforeBoundaryApprovalSubmission();
    const preventSubmission = validationResult.preventSubmission;
    const errors: string[] = validationResult.errors;

    return {
      valid: !preventSubmission,
      errors,
      defectiveGeometries: validationResult.defectiveGeometries,
      warningGeometries: validationResult.warningGeometries,
    };
  }
}
